// components/DocxEditor/DocxEditor.jsx
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './DocxEditor.css';
import 'boxicons/css/boxicons.min.css'; 
import '../../styles/fonts.css';
// Создаем экземпляр axios с базовыми настройками
const api = axios.create({
  baseURL: '/api',    
  withCredentials: true,
});

const DocxEditor = () => {
    const [zoom, setZoom] = useState(100);
    const [margins, setMargins] = useState({
        top: 25,
        bottom: 25,
        left: 30,
        right: 20
    });
    const [fmt, setFmt] = useState({
        bold: false,
        italic: false,
        underline: false,
        align: 'left',            // 'left' | 'center' | 'right' | 'justify'
        ol: false,                // ordered list
        ul: false,                // unordered list
    });

    const [fontSize, setFontSize] = useState('14px');
    const [lineHeightOverride, setLineHeightOverride] = useState(null);
    const [lineHeight, setLineHeight] = useState('1.5');
    const [isLoading, setIsLoading] = useState(false);
    const [fileName, setFileName] = useState('');
    
    const documentRef = useRef(null);
    const floatingToolbarRef = useRef(null);
    const documentContainerRef = useRef(null);
    const fileInputRef = useRef(null);
    const documentAreaRef = useRef(null);
    const prevZoomRef = useRef(100);

    const [mode, setMode] = useState('edit'); // 'edit' | 'template_fill'
    const [templates, setTemplates] = useState([]);
    const [templateTitle, setTemplateTitle] = useState('');

    const [tagName, setTagName] = useState('tag');
    const [tagType, setTagType] = useState('text'); // 'text' | 'number' | 'date'

    useEffect(() => {
        const onSelectionChange = () => refreshToolbar();

        const onPointerDown = (e) => {
            const tb = floatingToolbarRef.current;
            const docEl = documentRef.current;
            const t = e.target;

            // клик внутри панели — не скрываем
            if (tb && tb.contains(t)) return;

            // клик внутри документа: дадим пользователю выделить — позиционируем на mouseup
            if (docEl && docEl.contains(t)) return;

            // клик вне — скрываем
            hideToolbar();
        };

        const onKeyUp = (e) => {
            if (e.key === 'Escape') hideToolbar();
        };

        document.addEventListener('selectionchange', onSelectionChange);
        window.addEventListener('pointerdown', onPointerDown, true);
        window.addEventListener('keyup', onKeyUp);
        documentAreaRef.current?.addEventListener('scroll', hideToolbar);
        window.addEventListener('resize', hideToolbar);

        return () => {
            document.removeEventListener('selectionchange', onSelectionChange);
            window.removeEventListener('pointerdown', onPointerDown, true);
            window.removeEventListener('keyup', onKeyUp);
            documentAreaRef.current?.removeEventListener('scroll', hideToolbar);
            window.removeEventListener('resize', hideToolbar);
        };
    }, []);

    useEffect(() => {
        const root = documentRef.current;
        if (!root) return;

        if (mode === 'template_fill') {
            root.setAttribute('data-mode', 'fill');
            prepareTagsForFill();
            enableFillGuards();
            root.querySelectorAll('.doc-tag').forEach(validateTagHostValue);
        } else {
            root.removeAttribute('data-mode');
            disableFillGuards();
        }
        return () => disableFillGuards();
    }, [mode]);

    useEffect(() => {
        const onSelectionChange = () => refreshFormatState();
        const onKeyUp = () => refreshFormatState();
        const onMouseUp = () => setTimeout(refreshFormatState, 0);

        document.addEventListener('selectionchange', onSelectionChange);
        window.addEventListener('keyup', onKeyUp);
        // на сам документ (редактор)
        documentRef.current?.addEventListener('mouseup', onMouseUp);

        return () => {
            document.removeEventListener('selectionchange', onSelectionChange);
            window.removeEventListener('keyup', onKeyUp);
            documentRef.current?.removeEventListener('mouseup', onMouseUp);
        };
    }, []);

    // Эффект для инициализации полей документа
    useEffect(() => {
        updateDocumentMargins();
    }, [margins]);


    // Эффект для добавления обработчиков колесика мыши
    useEffect(() => {
        const handleWheel = (e) => {
            if (!e.ctrlKey) return;
            e.preventDefault();
            e.stopPropagation();

            const step = 5;
            const target = e.deltaY < 0 ? Math.min(150, zoom + step) : Math.max(50, zoom - step);
            applyZoom(target, { x: e.clientX, y: e.clientY });
        };

        const area = documentAreaRef.current;
        if (area) area.addEventListener('wheel', handleWheel, { passive: false });
        return () => area && area.removeEventListener('wheel', handleWheel);
    }, [zoom]);

    useEffect(() => {
    try { document.execCommand('styleWithCSS', true); } catch {}
    }, []);

    const updateDocumentMargins = () => {
        if (documentRef.current) {
            documentRef.current.style.paddingTop = `${margins.top}mm`;
            documentRef.current.style.paddingBottom = `${margins.bottom}mm`;
            documentRef.current.style.paddingLeft = `${margins.left}mm`;
            documentRef.current.style.paddingRight = `${margins.right}mm`;
        }
    };

    // В пикселях размеры «листа» (A4 под 96dpi: 794x1122 — как у вас)
    const DOC_W = 794;
    const DOC_H = 1122;

    const applyZoom = (nextZoom, anchor) => {
        const area = documentAreaRef.current;
        const container = documentContainerRef.current;
        if (!area || !container) return;

        const prevZoom = prevZoomRef.current;
        const prevScale = prevZoom / 100;
        const nextScale = nextZoom / 100;

        // 1) где якорь в координатах контента (до масштабирования)
        const areaRect = area.getBoundingClientRect();
        const anchorX = anchor?.x ?? (areaRect.left + area.clientWidth / 2);
        const anchorY = anchor?.y ?? (areaRect.top  + area.clientHeight / 2);

        // Позиция якоря с учётом текущего скролла
        const contentX = (area.scrollLeft + (anchorX - areaRect.left)) / prevScale;
        const contentY = (area.scrollTop  + (anchorY - areaRect.top )) / prevScale;

        // 2) Применяем масштаб и выставляем фактические размеры
        container.style.transformOrigin = '0 0';
        container.style.transform = `scale(${nextScale})`;
        container.style.width  = `${DOC_W}px`;            // базовые
        container.style.height = `${DOC_H}px`;
        // Чтобы у скролла была «видимая» высота под масштаб:
        container.style.minWidth  = `${DOC_W * nextScale}px`;
        container.style.minHeight = `${DOC_H * nextScale}px`;
        // (min* можете заменить на width/height отдельного «zoom-layer», если хотите чище)

        // 3) Возвращаем якорь в ту же экранную точку
        const newScrollLeft = contentX * nextScale - (anchorX - areaRect.left);
        const newScrollTop  = contentY * nextScale - (anchorY - areaRect.top);

        area.scrollLeft = Math.max(0, newScrollLeft);
        area.scrollTop  = Math.max(0, newScrollTop);

        // 4) обновляем состояние
        setZoom(nextZoom);
        prevZoomRef.current = nextZoom;
    };

    const fetchTemplates = async () => {
        const { data } = await api.get('/api/templates/');
        setTemplates(data || []);
    };
    
    const saveAsTemplate = async () => {
        const root = documentRef.current;
        if (!root) return;
        const title = templateTitle || (fileName ? `Шаблон: ${fileName}` : 'Новый шаблон');
        const html_content = root.innerHTML;
        const { data } = await api.post('/api/templates/save/', { title, html_content });
        alert(`Сохранено как шаблон: ${data.title}`);
        fetchTemplates();
    };
    
    const loadTemplate = async (id) => {
        const { data } = await api.get(`/api/templates/${id}/`);
        if (documentRef.current) documentRef.current.innerHTML = data.html_content;

        // подготовить теги и включить режим заполнения
        prepareTagsForFill();
        setMode('template_fill');
        enableFillGuards();
        // первичная подсветка/валидация
        documentRef.current.querySelectorAll('.doc-tag').forEach(validateTagHostValue);
    };

    // Возвращает .doc-tag, если И anchor, и focus строго внутри одного тега
    const getStrictTagHostFromSelection = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return null;

        const getHost = (node) => {
            if (!node) return null;
            let el = node.nodeType === 1 ? node : node.parentElement;
            while (el && el !== documentRef.current) {
            if (el.classList?.contains('doc-tag')) return el;
            el = el.parentElement;
            }
            return null;
        };

        const a = getHost(sel.anchorNode);
        const f = getHost(sel.focusNode);
        return a && a === f ? a : null;
    };

    // Разрешённые inputType внутри тега
    const ALLOWED_INPUT_TYPES = new Set([
        'insertText',
        'insertReplacementText',
        'insertFromPaste',
        'insertFromPasteAsPlainText',
        'deleteContentBackward',
        'deleteContentForward'
    ]);

    // Обработчики форматирования
    const handleFormat = (command, value = null) => {
        document.execCommand(command, false, value);
        // Небольшая задержка, чтобы DOM применил формат на выделении
        setTimeout(() => {
            refreshFormatState();
        }, 0);
    };

    const handleAlign = (where) => {
        const map = { left: 'justifyLeft', center: 'justifyCenter', right: 'justifyRight', justify: 'justifyFull' };
        document.execCommand(map[where], false, null);
        setTimeout(refreshFormatState, 0);
    };

    const insertTagAtSelection = ({ name, type }) => {
        if (!name || !isSelectionInsideDoc()) return;
        const sel = window.getSelection();
        const range = sel.getRangeAt(0);
        const span = document.createElement('span');
        span.className = 'doc-tag';
        span.setAttribute('data-tag', name);
        span.setAttribute('data-type', type || 'text');
        span.setAttribute('data-placeholder', `##${name}##`);
        span.setAttribute('data-empty', 'true');
        span.setAttribute('contenteditable', 'true');
        span.textContent = '\u200b';
        range.deleteContents();
        range.insertNode(span);
        sel.removeAllRanges();
        const r = document.createRange(); r.selectNodeContents(span); r.collapse(true); sel.addRange(r);
    };

    const isInsideTag = (node) => {
        if (!node) return null;
        let el = node.nodeType === 1 ? node : node.parentElement;
        while (el && el !== documentRef.current) {
            if (el.classList?.contains('doc-tag')) return el;
            el = el.parentElement;
        }
        return null;
    };

    const enableFillGuards = () => {
        const root = documentRef.current;
        if (!root) return;

        // Разрешённые типы для ввода текста внутри тега
        const ALLOWED_INPUT_TYPES = new Set([
            'insertText',
            'insertReplacementText',
            'insertFromPaste',
            'insertFromPasteAsPlainText',
            'deleteContentBackward',
            'deleteContentForward'
        ]);

        const onMouseDown = (e) => {
            const targetTag = e.target.closest?.('.doc-tag');
            if (targetTag) {
            e.preventDefault();
            ensureZWSP(targetTag);
            placeCaret(targetTag, true);
            return;
            }
            // вне тега — не даём ставить каретку в документ
            e.preventDefault();
            highlightEmptyTags();
        };

        // 🔑 Жёсткий контроль перед изменением
        const onBeforeInput = (e) => {
            const host = getStrictTagHostFromSelection();
            if (!host) { e.preventDefault(); return; }

            const it = e.inputType || '';

            // Только базовые операции внутри тега
            if (!ALLOWED_INPUT_TYPES.has(it)) { e.preventDefault(); return; }

            // Вставка текста: чистим якорь и валидируем
            if (it.startsWith('insert')) {
            ensureZWSP(host);
            if (e.data) {
                const typ = host.getAttribute('data-type') || 'text';
                if (host.textContent === '\u200b') host.textContent = '';
                if (typ === 'number' && !/[\d\-\.,]/.test(e.data)) { e.preventDefault(); return; }
            }
            setTimeout(() => validateTagHostValue(host), 0);
            return;
            }

            // Удаление: не даём “съесть” сам тег
            if (it === 'deleteContentBackward' || it === 'deleteContentForward') {
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) { e.preventDefault(); return; }
            const r = sel.getRangeAt(0);

            // Если удаляем «последний» символ — просто возвращаем якорь и стопим событие
            const plain = (host.textContent || '').replace(/\u200b/g,'');
            if (plain.length <= 1) {
                e.preventDefault();
                host.textContent = '\u200b';
                host.setAttribute('data-empty','true');
                placeCaret(host, it === 'deleteContentBackward' ? false : true);
                host.dataset.invalid = 'false';
                host.style.boxShadow = 'none';
                return;
            }

            // Если курсор на абсолютной границе — гасим, чтобы не выскочить за тег
            const isAtStart = (r.startContainer === host.firstChild && r.startOffset === 0);
            const isAtEnd   = (r.endContainer   === host.firstChild && r.endOffset === host.firstChild.length);
            if ((it === 'deleteContentBackward' && isAtStart) || (it === 'deleteContentForward' && isAtEnd)) {
                e.preventDefault();
                return;
            }

            // Иначе разрешаем, но потом валидируем
            setTimeout(() => validateTagHostValue(host), 0);
            return;
            }
        };

        // Чистая текстовая вставка
        const onPaste = (e) => {
            const host = getStrictTagHostFromSelection();
            if (!host) { e.preventDefault(); return; }
            e.preventDefault();
            const text = (e.clipboardData.getData('text') || '').trim();
            ensureZWSP(host);
            if (host.textContent === '\u200b') host.textContent = '';
            document.execCommand('insertText', false, text);
            validateTagHostValue(host);
        };

        // Блокируем форматные шорткаты и выход за пределы тега клавишами
        const onKeyDown = (e) => {
            const host = getStrictTagHostFromSelection();

            // Запрет Enter
            if (e.key === 'Enter') { e.preventDefault(); return; }

            // Ctrl/Cmd + B/I/U/Z/Y — запрещаем
            if ((e.ctrlKey || e.metaKey) && ['b','i','u','z','y'].includes(e.key.toLowerCase())) {
            e.preventDefault(); return;
            }

            if (!host) {
            // Вне тега — блокируем печать/удаление
            if (e.key === 'Backspace' || e.key === 'Delete' || e.key.length === 1) e.preventDefault();
            return;
            }

            // Доп.блок на границах, до того как браузер что-то удалит
            const sel = window.getSelection();
            if (sel && sel.rangeCount) {
            const r = sel.getRangeAt(0);
            const atStart = (r.startContainer === host.firstChild && r.startOffset === 0);
            const atEnd   = (r.endContainer   === host.firstChild && r.endOffset === host.firstChild.length);
            if ((e.key === 'Backspace' && atStart) || (e.key === 'Delete' && atEnd)) {
                e.preventDefault();
            }
            }
        };

        const onDrop = (e) => {
            const host = getStrictTagHostFromSelection();
            if (!host) { e.preventDefault(); return; }
            e.preventDefault();
            const text = (e.dataTransfer.getData('text') || '').trim();
            ensureZWSP(host);
            if (host.textContent === '\u200b') host.textContent = '';
            document.execCommand('insertText', false, text);
            validateTagHostValue(host);
        };

        // Подстраховка: если вдруг тег остался “пустым” после любого ввода — вернём якорь
        const onInput = () => {
            const host = getStrictTagHostFromSelection();
            if (!host) return;
            ensureZWSP(host);
            validateTagHostValue(host);
        };

        root.addEventListener('mousedown', onMouseDown, true);
        root.addEventListener('beforeinput', onBeforeInput);
        root.addEventListener('paste', onPaste);
        root.addEventListener('keydown', onKeyDown);
        root.addEventListener('drop', onDrop);
        root.addEventListener('input', onInput);

        root._fillGuards = { onMouseDown, onBeforeInput, onPaste, onKeyDown, onDrop, onInput };
    };

    const disableFillGuards = () => {
        const root = documentRef.current;
        const g = root?._fillGuards;
        if (!root || !g) return;
        root.removeEventListener('mousedown', g.onMouseDown, true);
        root.removeEventListener('beforeinput', g.onBeforeInput);
        root.removeEventListener('paste', g.onPaste);
        root.removeEventListener('keydown', g.onKeyDown);
        root.removeEventListener('drop', g.onDrop);
        root._fillGuards = null;
    };

    const validateTagInput = (host, e) => {
        const typ = host.getAttribute('data-type');
        if (!typ) return;
        if (typ === 'number' && e.data) {
        // Разрешим цифры, минус, точку, запятую — остальное блокируем
        if (!/[\d\-\.,]/.test(e.data)) e.preventDefault();
        }
        // для даты позволяем ввод, проверим после
        setTimeout(() => validateTagHostValue(host), 0);
    };

    const validateTagHostValue = (host) => {
        const typ = host.getAttribute('data-type') || 'text';
        const val = (host.textContent || '').replace(/\u200b/g,'').trim();
        if (val) host.removeAttribute('data-empty'); else host.setAttribute('data-empty','true');

        if (typ === 'number') {
            const norm = val.replace(',', '.');
            const ok = norm === '' || /^[-+]?\d+(\.\d+)?$/.test(norm);
            host.dataset.invalid = ok ? 'false' : 'true';
            host.style.boxShadow = ok ? 'none' : 'inset 0 0 0 2px rgba(255,0,0,.5)';
        } else if (typ === 'date') {
            const ok = val === '' || /^\d{4}-\d{2}-\d{2}$/.test(val) || /^\d{2}\.\d{2}\.\d{4}$/.test(val);
            host.dataset.invalid = ok ? 'false' : 'true';
            host.style.boxShadow = ok ? 'none' : 'inset 0 0 0 2px rgba(255,0,0,.5)';
        } else {
            host.dataset.invalid = 'false';
            host.style.boxShadow = 'none';
    }
    };

    const highlightEmptyTags = () => {
        const root = documentRef.current;
        if (!root) return;
        root.querySelectorAll('.doc-tag').forEach(el => {
        validateTagHostValue(el);
        });
    };

    const replaceFontTagsInSelection = (sizePx) => {
        const root = documentRef.current;
        if (!root) return;

        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);

        const fonts = Array.from(root.querySelectorAll('font[size="7"]'))
            .filter(node => { try { return range.intersectsNode(node); } catch { return false; } });

        fonts.forEach(fontEl => {
            const span = document.createElement('span');
            span.style.fontSize = sizePx;
            while (fontEl.firstChild) span.appendChild(fontEl.firstChild);
            if (fontEl.getAttribute('style')) {
            span.setAttribute('style', `${span.getAttribute('style')}; ${fontEl.getAttribute('style')}`);
            }
            fontEl.parentNode.replaceChild(span, fontEl);
        });

        // схлопываем одинаковые соседние спаны
        mergeAdjacentFontSpans(root, sizePx);
    };

    const normalizeKeywordFontSizesInSelection = (sizePx) => {
        const root = documentRef.current;
        if (!root) return;
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);

        const candidates = Array.from(
            root.querySelectorAll('span[style*="font-size"], *[style*="font-size"]')
        ).filter(node => {
            try { return range.intersectsNode(node); } catch { return false; }
        });

        const keywordRe = /(?:^|\s)(xx-small|x-small|small|medium|large|x-large|xx-large|xxx-large|-webkit-xxx-large|larger|smaller|\d+%)(?:;|$)/i;
        candidates.forEach(el => {
            const st = el.style;
            if (!st || !st.fontSize) return;
            if (keywordRe.test(st.fontSize)) {
            st.fontSize = sizePx; // принудительно в px
            }
        });

        // на всякий — схлопнуть одинаковые спаны
        mergeAdjacentFontSpans(root, sizePx);
    };

    const handleFontSizeChange = (sizePx) => {
        setFontSize(sizePx);

        const root = documentRef.current;
        if (!root) return;

        // Гарантируем, что каретка/выделение внутри редактора
        if (!isSelectionInsideDoc()) {
            root.focus();
            const sel = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(root);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }

        // ВРЕМЕННО выключаем styleWithCSS, чтобы получить <font size="7">
        try { document.execCommand('styleWithCSS', false); } catch {}
        try { document.execCommand('fontSize', false, '7'); } catch {}
        // Возвращаем styleWithCSS= true для остального форматирования
        try { document.execCommand('styleWithCSS', true); } catch {}

        // Заменяем <font size="7"> на <span style="font-size: ...">
        replaceFontTagsInSelection(sizePx);

        // Если браузер всё же нагенерил keyword/percent — нормализуем в px
        normalizeKeywordFontSizesInSelection(sizePx);

        setTimeout(refreshFormatState, 0);
    };

    const handleLineHeightChange = (height) => {
        setLineHeight(height);

        // Применяем line-height как override только к корневому контейнеру,
        // не трогая стили, уже заданные на отдельных <p> и <li>.
        if (documentRef.current) {
            documentRef.current.style.lineHeight = height ? height : null;

            // Чтобы не было "проброса" line-height внутрь дочерних элементов с инлайновыми стилями
            documentRef.current.querySelectorAll('p, li').forEach(el => {
                if (el.style.lineHeight) return; // у абзаца уже есть line-height из бэкенда
                el.style.lineHeight = height;
            });
        }
    };

    const handleMarginChange = (type, value) => {
        setMargins(prev => ({
            ...prev,
            [type]: parseInt(value) || 0
        }));
    };

    const zoomAroundViewportCenter = (targetZoom) => {
    const area = documentAreaRef.current;
    if (!area) return;
    const rect = area.getBoundingClientRect();
    applyZoom(targetZoom, { x: rect.left + rect.width/2, y: rect.top + rect.height/2 });
    };

    const handleZoomIn = () => zoomAroundViewportCenter(Math.min(150, zoom + 10));
    const handleZoomOut = () => zoomAroundViewportCenter(Math.max(50, zoom - 10));
    const handleZoomSlider = (val) => zoomAroundViewportCenter(parseInt(val, 10));

    // Загрузка DOCX файла с использованием axios
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Проверяем расширение файла
        if (!file.name.toLowerCase().endsWith('.docx')) {
            alert('Пожалуйста, выберите файл с расширением .docx');
            return;
        }

        setIsLoading(true);
        setFileName(file.name);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('config', JSON.stringify({
                preserve_headers: true,
                preserve_lists: true,
                preserve_tables: true,
                preserve_images: true,
                preserve_hyperlinks: true,
                inline_styles: true
            }));

            const response = await api.post('/api/convert/docx-to-html/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-CSRFToken': getCsrfToken(),
                },
                timeout: 30000, // 30 секунд таймаут
            });

            if (response.data.success && response.data.html_content) {
                if (documentRef.current) {
                    documentRef.current.innerHTML = response.data.html_content;
                }
                // NEW: применяем поля документа, если пришли
                const m = response.data.page_margins_mm;
                if (m && typeof m.top === 'number' && typeof m.left === 'number'
                    && typeof m.right === 'number' && typeof m.bottom === 'number') {
                    setMargins({
                        top: Math.round(m.top),
                        bottom: Math.round(m.bottom),
                        left: Math.round(m.left),
                        right: Math.round(m.right)
                    });
                }
            } else {
                throw new Error(response.data.error || 'Conversion failed');
            }

        } catch (error) {
            console.error('Error converting DOCX:', error);
            
            let errorMessage = 'Ошибка при конвертации файла';
            if (error.response) {
                // Сервер ответил с ошибкой
                errorMessage = `Ошибка сервера: ${error.response.status} - ${error.response.data.error || 'Unknown error'}`;
            } else if (error.request) {
                // Запрос был сделан, но ответ не получен
                errorMessage = 'Не удалось подключиться к серверу. Проверьте, запущен ли Django сервер на порту 4000.';
            } else {
                // Что-то пошло не так при настройке запроса
                errorMessage = `Ошибка: ${error.message}`;
            }
            
            alert(errorMessage);
        } finally {
            setIsLoading(false);
            // Очищаем input чтобы можно было загрузить тот же файл снова
            event.target.value = '';
        }
    };

    // Всегда держим в теге невидимый якорь, если он пуст
    const ensureZWSP = (host) => {
        if (!host) return;
        const txt = (host.textContent || '').replace(/\u200b/g,'');
        if (txt.length === 0) {
            host.textContent = '\u200b';
            host.setAttribute('data-empty', 'true');
        }
    };

    // Поставить каретку в начало/конец host
    const placeCaret = (host, atEnd = true) => {
        if (!host) return;
        if (!host.firstChild) host.appendChild(document.createTextNode('\u200b'));
        const tn = host.firstChild;
        const range = document.createRange();
        const pos = atEnd ? (tn.length) : 0;
        range.setStart(tn, Math.min(pos, tn.length));
        range.collapse(true);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
    };

    const getCsrfToken = () => {
        // Для разработки можно временно возвращать пустую строку
        // или получить токен через отдельный запрос
        const cookieValue = document.cookie
            .split('; ')
            .find(row => row.startsWith('csrftoken='))
            ?.split('=')[1];
        return cookieValue || '';
    };

    const handleLoadClick = () => {
        fileInputRef.current?.click();
    };

    // Плавающая панель инструментов
    // --- floating toolbar control ---
    const hideToolbar = () => {
        const tb = floatingToolbarRef.current;
        if (tb) tb.style.display = 'none';
    };

    const isSelectionInDocument = (sel) => {
        const docEl = documentRef.current;
        if (!sel || !docEl || sel.rangeCount === 0) return false;
        const node = sel.getRangeAt(0).commonAncestorContainer;
        const el = node.nodeType === 1 ? node : node.parentElement;
        return el && docEl.contains(el);
    };

    const readAlign = () => {
        // В браузерах align возвращается через queryCommandState для 'justifyLeft/Center/Right/Full'
        if (document.queryCommandState('justifyCenter')) return 'center';
        if (document.queryCommandState('justifyRight'))  return 'right';
        if (document.queryCommandState('justifyFull'))   return 'justify';
        // По умолчанию считаем left
        return 'left';
    };

    const refreshFormatState = () => {
    const sel = window.getSelection();
        if (!sel || !isSelectionInDocument(sel)) {
            // нет выделения в документе — можно сбросить или оставить как есть
            setFmt((prev) => ({ ...prev, /* опционально: */ }));
            return;
        }
        
        setFmt({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
            align: readAlign(),
            ol: document.queryCommandState('insertOrderedList'),
            ul: document.queryCommandState('insertUnorderedList'),
        });
    };

    const positionToolbarOverSelection = () => {
    const tb = floatingToolbarRef.current;
    const sel = window.getSelection();
    if (!tb || !sel || sel.rangeCount === 0) return;

    // геометрия выделения
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();

    // если прямоугольник нулевой (иногда так бывает), не показываем
    if (!rect || (rect.width === 0 && rect.height === 0)) {
        hideToolbar();
        return;
    }

    // позиционируем по центру выделения с отступом 12px сверху
    const x = rect.left + rect.width / 2 - tb.offsetWidth / 2;
    const y = rect.top - tb.offsetHeight - 12;

    // границы экрана
    const pad = 8;
    const left = Math.max(pad, Math.min(x, window.innerWidth - tb.offsetWidth - pad));
    const top  = Math.max(pad, y < pad ? rect.bottom + 12 : y);

    tb.style.left = `${left}px`;
    tb.style.top  = `${top}px`;
    tb.style.display = 'flex';
    };

    const refreshToolbar = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !isSelectionInDocument(sel)) {
        hideToolbar();
        return;
    }
    // даём раскрывшимся шрифтам/DOMу пересчитаться перед offsetWidth
    requestAnimationFrame(() => positionToolbarOverSelection());
    };

    const updateActiveButtons = () => {
        // Здесь можно добавить логику обновления состояния кнопок
        // на основе document.queryCommandState
    };

    const handleSave = () => {
        alert('Функция сохранения в DOCX будет реализована позже');
    };

    const handleNewDocument = () => {
        if (documentRef.current) {
            documentRef.current.innerHTML = `
                <h1>Новый документ</h1>
                <p>Начните вводить текст здесь...</p>
            `;
        }
        setFileName('');
    };

    // Возвращает true, если текущая selection внутри редактора
    const isSelectionInsideDoc = () => {
        const root = documentRef.current;
        const sel = window.getSelection();
        if (!root || !sel || sel.rangeCount === 0) return false;
        const node = sel.getRangeAt(0).commonAncestorContainer;
        const el = node.nodeType === 1 ? node : node.parentElement;
        return !!(el && root.contains(el));
    };

    // Сливает соседние span'ы с одинаковым font-size, чтобы не плодить вложенность
    const mergeAdjacentFontSpans = (root, sizePx) => {
        if (!root) return;
        const spans = Array.from(root.querySelectorAll('span'))
            .filter(s => s.style && s.style.fontSize === sizePx);
        spans.forEach((span) => {
            // слить с предыдущим
            const prev = span.previousSibling;
            if (prev && prev.nodeType === 1 && prev.tagName === 'SPAN' && prev.style && prev.style.fontSize === sizePx) {
                while (span.firstChild) prev.appendChild(span.firstChild);
                span.remove();
                span = prev;
            }
            // слить со следующим
            const next = span.nextSibling;
            if (next && next.nodeType === 1 && next.tagName === 'SPAN' && next.style && next.style.fontSize === sizePx) {
                while (next.firstChild) span.appendChild(next.firstChild);
                next.remove();
            }
        });
    };

    // Оборачивает ТЕКУЩЕЕ выделение в <span style="font-size: …">
    // Работает и при «рваной» структуре: через extractContents/surroundContents с фолбеком
    const wrapSelectionWithFontSize = (sizePx) => {
        const root = documentRef.current;
        if (!root) return;

        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const range = sel.getRangeAt(0);

        if (sel.isCollapsed) {
            // Пустая каретка — вставим «стилизованный маркер», чтобы дальше печатать этим размером
            const span = document.createElement('span');
            span.style.fontSize = sizePx;
            // zero-width space — сразу переносим каретку внутрь
            const zw = document.createTextNode('\u200b');
            span.appendChild(zw);
            range.insertNode(span);

            // Переносим каретку внутрь span после маркера
            sel.removeAllRanges();
            const newRange = document.createRange();
            newRange.setStart(zw, 1);
            newRange.collapse(true);
            sel.addRange(newRange);
            return;
        }

        // Непустое выделение: пытаемся аккуратно обернуть
        try {
            // surroundContents падает, если выделение пересекает частично теги.
            const wrapper = document.createElement('span');
            wrapper.style.fontSize = sizePx;
            range.surroundContents(wrapper);
        } catch {
            // Фолбек: через extract/insert (надежнее с «рваными» диапазонами)
            const fragment = range.cloneContents();
            const wrapper = document.createElement('span');
            wrapper.style.fontSize = sizePx;
            wrapper.appendChild(fragment);
            range.deleteContents();
            range.insertNode(wrapper);

            // Выделим только что вставленный wrapper (чтобы UX был ожидаемый)
            sel.removeAllRanges();
            const newRange = document.createRange();
            newRange.selectNodeContents(wrapper);
            sel.addRange(newRange);
        }

        // Небольшая уборка — слить соседние спаны с тем же размером
        mergeAdjacentFontSpans(root, sizePx);
    };

    const prepareTagsForFill = () => {
        const root = documentRef.current;
        if (!root) return;

        // 1) Оборачиваем «##name##» в <span class="doc-tag"...> (на случай старых шаблонов)
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
        const toWrap = [];
        const re = /##([a-zA-Z_][a-zA-Z0-9_]*)##/g;
        while (walker.nextNode()) {
            const node = walker.currentNode;
            if (!node.nodeValue || !re.test(node.nodeValue)) continue;
            toWrap.push(node);
        }
        toWrap.forEach(textNode => {
            const frag = document.createDocumentFragment();
            let s = textNode.nodeValue;
            let match;
            re.lastIndex = 0;
            while ((match = re.exec(s))) {
            const before = s.slice(0, match.index);
            if (before) frag.appendChild(document.createTextNode(before));
            const name = match[1];
            const span = document.createElement('span');
            span.className = 'doc-tag';
            span.textContent = '\u200b'; // важно: есть текстовый узел
            span.setAttribute('data-empty','true');
            span.setAttribute('contenteditable','true');
            span.setAttribute('data-tag', name);
            span.setAttribute('data-type', 'text');
            span.setAttribute('data-placeholder', `##${name}##`);
            frag.appendChild(span);
            s = s.slice(match.index + match[0].length);
            re.lastIndex = 0;
            }
            if (s) frag.appendChild(document.createTextNode(s));
            textNode.parentNode.replaceChild(frag, textNode);
        });

        // 2) Нормализуем уже существующие .doc-tag
        root.querySelectorAll('.doc-tag').forEach(tag => {
            tag.setAttribute('contenteditable', 'true');
            if (!tag.getAttribute('data-type')) tag.setAttribute('data-type', 'text');
            if (!tag.getAttribute('data-placeholder')) {
            const name = tag.getAttribute('data-tag') || 'field';
            tag.setAttribute('data-placeholder', `##${name}##`);
            }
            if (!tag.firstChild) tag.appendChild(document.createTextNode('\u200b'));
            // пустой?
            const val = tag.textContent.replace(/\u200b/g,'').trim();
            if (val.length === 0) tag.setAttribute('data-empty','true'); else tag.removeAttribute('data-empty');
            // удобство фокуса
            tag.setAttribute('tabindex', '0');
        });
    };

    const getHostFromSelection = () => {
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return null;
        const r = sel.getRangeAt(0);

        // 1) если уже внутри .doc-tag
        let host = isInsideTag(r.startContainer);
        if (host) return host;

        // 2) если на границе: ищем соседний .doc-tag
        const snapInto = (candidate, placeAtEnd=false) => {
            if (!candidate) return null;
            if (!(candidate.nodeType === 1 && candidate.classList?.contains('doc-tag'))) return null;
            if (!candidate.firstChild) candidate.appendChild(document.createTextNode('\u200b'));
            const tn = candidate.firstChild;
            const nr = document.createRange();
            nr.setStart(tn, placeAtEnd ? tn.length : 0);
            nr.collapse(true);
            sel.removeAllRanges(); sel.addRange(nr);
            return candidate;
        };

        // 2a) когда контейнер — элемент (как раньше)
        if (r.startContainer.nodeType === 1) {
            const el = r.startContainer;
            const next = el.childNodes[r.startOffset] || null;
            const prev = el.childNodes[r.startOffset - 1] || null;
            return snapInto(next, false) || snapInto(prev, true);
        }

        // 2b) когда контейнер — ТЕКСТОВЫЙ узел
        if (r.startContainer.nodeType === 3) {
            const text = r.startContainer;
            const parent = text.parentNode;
            // если курсор в конце текстового узла — смотрим следующий sibling
            if (r.startOffset === text.length) {
            return snapInto(text.nextSibling, false);
            }
            // если в начале узла — смотрим предыдущий sibling
            if (r.startOffset === 0) {
            return snapInto(text.previousSibling, true);
            }
        }

        return null;
    };

    return (
        <div className="docx-editor-container">
            {/* Скрытый input для загрузки файлов */}
            <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".docx,.doc"
            style={{ display: 'none' }}
            />

            <header>
            <div className="logo-container">
                <svg className="logo" viewBox="-1.5 0 255 255" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid">
                    <defs>
                        <style>
                        {`.cls-3 { fill: #4cebc0; }
                            .cls-4 { fill: #00e2a5; }
                            .cls-5 { fill: #fc9502; }
                            .cls-6 { fill: #ff5134; }`}
                        </style>
                    </defs>
                    <g id="carrot"> 
                        <path d="M240.543,81.794 C240.543,81.794 160.080,103.354 160.080,103.354 C160.080,103.354 148.286,91.560 
                        148.286,91.560 C148.286,91.560 169.846,11.097 169.846,11.097 C171.990,3.095 180.215,-1.654 188.217,0.491 
                        C196.219,2.635 200.968,10.860 198.824,18.862 C198.824,18.862 186.395,65.245 186.395,65.245 C186.395,65.245 
                        232.778,52.816 232.778,52.816 C240.780,50.672 249.005,55.421 251.150,63.423 C253.294,71.425 248.545,79.650 
                        240.543,81.794 Z" id="path-1" className="cls-3" fillRule="evenodd"/> 
                        <path d="M237.622,14.018 C243.480,19.876 243.480,29.374 237.622,35.232 C237.622,35.232 170.447,102.407 
                        170.447,102.407 C170.447,102.407 149.233,81.194 149.233,81.194 C149.233,81.194 216.409,14.018 
                        216.409,14.018 C222.266,8.161 231.764,8.161 237.622,14.018 Z" id="path-2" className="cls-4" fillRule="evenodd"/> 
                        <path d="M199.248,152.901 C196.419,154.315 26.904,250.192 26.904,250.192 C17.827,255.309 
                        10.525,256.441 4.276,250.192 C-1.972,243.944 -0.841,236.642 4.276,227.565 C4.276,227.565 
                        100.153,58.049 101.568,55.221 C102.982,52.392 112.625,38.929 125.899,52.202 C125.899,52.202 
                        202.266,128.570 202.266,128.570 C215.540,141.843 202.076,151.487 
                        199.248,152.901 Z" id="path-3" className="cls-5" fillRule="evenodd"/> 
                        <path d="M132.970,152.611 C134.532,151.049 137.065,151.049 138.627,152.611 C138.627,152.611 
                        160.634,174.619 160.634,174.619 C158.288,175.943 155.874,177.305 153.402,178.701 
                        C153.402,178.701 132.970,158.268 132.970,158.268 C131.408,156.706 131.408,154.173 
                        132.970,152.611 ZM55.188,176.653 C55.188,176.653 79.230,200.695 79.230,200.695 
                        C80.792,202.257 80.792,204.789 79.230,206.352 C77.668,207.914 75.135,207.914 
                        73.573,206.352 C73.573,206.352 49.531,182.310 49.531,182.310 C47.969,180.748 
                        47.969,178.215 49.531,176.653 C51.093,175.091 53.626,175.091 55.188,176.653 
                        ZM31.146,212.008 C29.584,213.570 27.052,213.570 25.490,212.008 C25.490,212.008 
                        17.558,204.077 17.558,204.077 C18.851,201.791 20.217,199.375 21.645,196.850 
                        C21.645,196.850 31.146,206.352 31.146,206.352 C32.709,207.914 32.709,210.446 
                        31.146,212.008 ZM76.401,155.440 C76.401,155.440 56.371,135.410 56.371,135.410 
                        C57.738,132.991 59.101,130.579 60.456,128.180 C60.456,128.180 82.058,149.783 
                        82.058,149.783 C83.620,151.345 83.620,153.878 82.058,155.440 C80.496,157.002 
                        77.964,157.002 76.401,155.440 ZM142.162,108.064 C140.600,109.626 138.067,109.626 
                        136.505,108.064 C136.505,108.064 112.464,84.022 112.464,84.022 C110.902,82.460 
                        110.902,79.927 112.464,78.365 C114.026,76.803 116.559,76.803 118.121,78.365 
                        C118.121,78.365 142.162,102.407 142.162,102.407 C143.724,103.969 143.724,106.501 
                        142.162,108.064 Z" id="path-4" className="cls-6" fillRule="evenodd"/> 
                    </g>
                </svg>
                <h1>What's up, Doc?!</h1>
            </div>

            <div className="header-controls">
                <div className="document-controls">
                <button className="load-btn" onClick={handleLoadClick} disabled={isLoading} title="Загрузить DOCX">
                    <i className='bx bx-upload icon'></i>
                    {isLoading ? 'Загрузка...' : 'Загрузить DOCX'}
                </button>
                <button className="new-doc-btn" onClick={handleNewDocument} title="Новый документ">
                    <i className='bx bx-plus icon'></i> Новый
                </button>
                </div>

                <div className="zoom-control">
                <button className="format-btn" onClick={handleZoomOut} title="Уменьшить">
                    <i className='bx bx-zoom-out icon'></i>
                </button>
                <span className="zoom-value">{zoom}%</span>
                <button className="format-btn" onClick={handleZoomIn} title="Увеличить">
                    <i className='bx bx-zoom-in icon'></i>
                </button>
                <input
                    type="range"
                    min="50"
                    max="150"
                    value={zoom}
                    className="zoom-slider"
                    onChange={(e) => handleZoomSlider(e.target.value)}
                />
                </div>

                <button className="save-btn" onClick={handleSave}>
                <i className='bx bx-save icon'></i> Сохранить в DOCX
                </button>
            </div>
            </header>

            <div className="main-content">
            {/* ЛЕВЫЙ САЙДБАР: форматирование + Теги */}
            <div className="formatting-panel">
                {fileName && (
                <div className="file-info">
                    <h3>Текущий файл</h3>
                    <div className="file-name">{fileName}</div>
                </div>
                )}

                <div className="format-group">
                <h3>Теги</h3>
                <input
                    className="margin-input"
                    placeholder="имя (tag)"
                    value={tagName}
                    onChange={e => setTagName(e.target.value)}
                />
                <select className="format-btn" value={tagType} onChange={e => setTagType(e.target.value)}>
                    <option value="text">text</option>
                    <option value="number">number</option>
                    <option value="date">date</option>
                </select>
                <button
                    className="format-btn"
                    onMouseDown={(e)=>e.preventDefault()}
                    onClick={() => insertTagAtSelection({ name: tagName.trim(), type: tagType })}
                >
                    <i className='bx bx-purchase-tag-alt icon'></i> Вставить тег
                </button>
                </div>

                <div className="format-group">
                <h3>Размер шрифта</h3>
                <select
                    className="format-btn"
                    value={fontSize}
                    onChange={(e) => handleFontSizeChange(e.target.value)}
                >
                    <option value="12px">12px</option>
                    <option value="14px">14px</option>
                    <option value="16px">16px</option>
                    <option value="18px">18px</option>
                    <option value="20px">20px</option>
                    <option value="24px">24px</option>
                </select>
                </div>

                <div className="format-group">
                <h3>Стиль текста</h3>
                <div className="format-buttons">
                    <button className={`format-btn ${fmt.bold ? 'active' : ''}`} onClick={() => handleFormat('bold')} title="Жирный">
                    <i className='bx bx-bold icon' />
                    </button>
                    <button className={`format-btn ${fmt.italic ? 'active' : ''}`} onClick={() => handleFormat('italic')} title="Курсив">
                    <i className='bx bx-italic icon' />
                    </button>
                    <button className={`format-btn ${fmt.underline ? 'active' : ''}`} onClick={() => handleFormat('underline')} title="Подчеркивание">
                    <i className='bx bx-underline icon' />
                    </button>
                </div>
                </div>

                <div className="format-group">
                <h3>Выравнивание</h3>
                <div className="format-buttons">
                    <button className={`format-btn ${fmt.align === 'left' ? 'active' : ''}`} onClick={() => handleAlign('left')} title="По левому краю">
                    <i className='bx bx-align-left icon' />
                    </button>
                    <button className={`format-btn ${fmt.align === 'center' ? 'active' : ''}`} onClick={() => handleAlign('center')} title="По центру">
                    <i className='bx bx-align-middle icon' />
                    </button>
                    <button className={`format-btn ${fmt.align === 'right' ? 'active' : ''}`} onClick={() => handleAlign('right')} title="По правому краю">
                    <i className='bx bx-align-right icon' />
                    </button>
                    <button className={`format-btn ${fmt.align === 'justify' ? 'active' : ''}`} onClick={() => handleAlign('justify')} title="По ширине">
                    <i className='bx bx-align-justify icon' />
                    </button>
                </div>
                </div>

                <div className="format-group">
                <h3>Списки</h3>
                <div className="format-buttons">
                    <button className={`format-btn ${fmt.ul ? 'active' : ''}`} onClick={() => handleFormat('insertUnorderedList')} title="Маркированный">
                    <i className='bx bx-list-ul icon' />
                    </button>
                    <button className={`format-btn ${fmt.ol ? 'active' : ''}`} onClick={() => handleFormat('insertOrderedList')} title="Нумерованный">
                    <i className='bx bx-list-ol icon' />
                    </button>
                </div>
                </div>

                <div className="format-group">
                <h3>Межстрочный интервал</h3>
                <select
                    className="format-btn"
                    value={lineHeight}
                    onChange={(e) => handleLineHeightChange(e.target.value)}
                >
                    <option value="1">1.0</option>
                    <option value="1.15">1.15</option>
                    <option value="1.5">1.5</option>
                    <option value="2">2.0</option>
                </select>
                </div>

                <div className="format-group">
                <h3>Поля документа (мм)</h3>
                <div className="margin-controls">
                    <div className="margin-control">
                    <label htmlFor="marginTop">Сверху</label>
                    <input id="marginTop" type="number" className="margin-input" min="0" max="100" value={margins.top} onChange={(e) => handleMarginChange('top', e.target.value)} />
                    </div>
                    <div className="margin-control">
                    <label htmlFor="marginBottom">Снизу</label>
                    <input id="marginBottom" type="number" className="margin-input" min="0" max="100" value={margins.bottom} onChange={(e) => handleMarginChange('bottom', e.target.value)} />
                    </div>
                    <div className="margin-control">
                    <label htmlFor="marginLeft">Слева</label>
                    <input id="marginLeft" type="number" className="margin-input" min="0" max="100" value={margins.left} onChange={(e) => handleMarginChange('left', e.target.value)} />
                    </div>
                    <div className="margin-control">
                    <label htmlFor="marginRight">Справа</label>
                    <input id="marginRight" type="number" className="margin-input" min="0" max="100" value={margins.right} onChange={(e) => handleMarginChange('right', e.target.value)} />
                    </div>
                </div>
                </div>
            </div>

            {/* ЦЕНТР: документ */}
            <div className="document-area" ref={documentAreaRef}>
                <div className="zoom-layer" ref={documentContainerRef}>
                {isLoading ? (
                    <div className="loading-overlay">
                    <div className="loading-spinner">
                        <i className='bx bx-loader-alt bx-spin icon'></i>
                        <p>Конвертация DOCX...</p>
                    </div>
                    </div>
                ) : null}

                <div className="doc-wrapper">
                    {mode === 'template_fill' && (
                    <div className="fill-lock-icon" title="Режим заполнения">
                        <i className='bx bxs-lock-alt' />
                        Заполнение шаблона
                    </div>
                    )}

                    <div
                    className="document"
                    contentEditable
                    ref={documentRef}
                    onMouseUp={() => setTimeout(refreshToolbar, 0)}
                    style={ lineHeightOverride ? { lineHeight: lineHeightOverride } : undefined }
                    suppressContentEditableWarning
                    >
                    {!fileName && (
                        <>
                        <h1>Заголовок документа</h1>
                        <p>Это пример текста, который можно редактировать и форматировать. Выделите текст, чтобы применить к нему форматирование.</p>
                        <p>Вы можете изменить размер шрифта, сделать текст жирным, курсивом или подчеркнутым.</p>
                        <p>Также доступно выравнивание текста и создание списков.</p>
                        <ul className="bullet-list">
                            <li className="list-item">Пункт маркированного списка</li>
                            <li className="list-item">Еще один пункт</li>
                        </ul>
                        <ol className="number-list">
                            <li className="list-item">Первый пункт нумерованного списка</li>
                            <li className="list-item">Второй пункт</li>
                        </ol>
                        </>
                    )}
                    </div>
                </div>
                </div>
            </div>

            {/* ПРАВЫЙ САЙДБАР: Шаблоны */}
            <div className="templates-panel">
                <div className="templates-row" style={{justifyContent:'space-between', alignItems:'center'}}>
                <h3>Шаблоны</h3>
                <span className="templates-badge">{mode === 'template_fill' ? 'Заполнение' : 'Редактирование'}</span>
                </div>

                <input
                className="margin-input"
                placeholder="Название шаблона"
                value={templateTitle}
                onChange={(e)=>setTemplateTitle(e.target.value)}
                />

                <div className="templates-row">
                <button className="format-btn" onClick={saveAsTemplate}>
                    <i className='bx bx-save icon'></i> Сохранить как шаблон
                </button>
                <button className="format-btn" onClick={fetchTemplates} title="Обновить">
                    <i className='bx bx-refresh icon'></i>
                </button>
                </div>

                {mode === 'template_fill' ? (
                <button className="format-btn" onClick={() => { setMode('edit'); disableFillGuards(); }}>
                    <i className='bx bx-edit icon'></i> Выйти из режима заполнения
                </button>
                ) : null}

                <div className="templates-list">
                {templates.length === 0 && (
                    <div style={{color:'#888', fontSize:'.85rem'}}>Нет сохранённых шаблонов</div>
                )}
                {templates.map(t => (
                    <button key={t.id} className="format-btn" onClick={()=>loadTemplate(t.id)} title={`Открыть: ${t.title}`}>
                    {t.title}
                    </button>
                ))}
                </div>
            </div>
            </div>

            <FloatingToolbar
            ref={floatingToolbarRef}
            fmt={fmt}
            handleFormat={handleFormat}
            handleAlign={handleAlign}
            onInsertTag={() => insertTagAtSelection({ name: tagName, type: tagType })}
            />
        </div>
    );

};

// Компонент плавающей панели инструментов
const FloatingToolbar = React.forwardRef(({ fmt, handleFormat, handleAlign, onInsertTag }, ref) => {
  return (
    <div className="floating-toolbar" id="floatingToolbar" ref={ref}>
        <button
            className={`format-btn ${fmt.bold ? 'active' : ''}`}
            onClick={() => handleFormat('bold')}
            title="Жирный"
        >
            <i className='bx bx-bold icon'></i>
        </button>

        <button
            className={`format-btn ${fmt.italic ? 'active' : ''}`}
            onClick={() => handleFormat('italic')}
            title="Курсив"
        >
            <i className='bx bx-italic icon'></i>
        </button>

        <button
            className={`format-btn ${fmt.underline ? 'active' : ''}`}
            onClick={() => handleFormat('underline')}
            title="Подчеркивание"
        >
            <i className='bx bx-underline icon'></i>
        </button>

        <button
            className={`format-btn ${fmt.align === 'left' ? 'active' : ''}`}
            onClick={() => handleAlign('left')}
            title="По левому краю"
        >
            <i className='bx bx-align-left icon'></i>
        </button>

        <button
            className={`format-btn ${fmt.align === 'center' ? 'active' : ''}`}
            onClick={() => handleAlign('center')}
            title="По центру"
        >
            <i className='bx bx-align-middle icon'></i>
        </button>

        <button
            className={`format-btn ${fmt.align === 'right' ? 'active' : ''}`}
            onClick={() => handleAlign('right')}
            title="По правому краю"
        >
            <i className='bx bx-align-right icon'></i>
        </button>
        
        <button
            className={`format-btn ${fmt.align === 'justify' ? 'active' : ''}`}
            onClick={() => handleAlign('justify')}
            title="По ширине"
        >
            <i className='bx bx-align-justify icon'></i>
        </button>
        
        <button
            className="format-btn"
            onMouseDown={(e)=>e.preventDefault()} // чтобы не сбивать выделение
            onClick={onInsertTag}
            title="Вставить тег"
            >
            <i className='bx bx-purchase-tag-alt icon'></i>
        </button>

    </div>
  );
});

export default DocxEditor;